import React, { useState, useEffect } from 'react';
import './EditarFornecedor.css';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { message, Button } from 'antd';
import ProtectedRoute from '../../../../components/ProtectedRoute';
import api from '../../../../services/api';
import API_CONFIG from '../../../../config/api.config';

const EditarFornecedor = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const initialState = {
    razaoSocial: '',
    cnpj: '',
    email: '',
    celular: '',
    telefone: '',
    logradouro: '',
    pais: 'Brasil',
    estado: '',
    cidade: '',
    cep: '',
    id: null,
    status: ''
  };

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);

  // Helper function to convert to Title Case and remove accents
  const toTitleCase = (phrase) => {
    if (!phrase) return '';
    return phrase
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Sessão expirada. Por favor, faça login novamente.');
        navigate('/login');
        setLoading(false);
        return;
      }

      let sortedEstados = [];

      try {
        const estadosRes = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
        if (!estadosRes.ok) throw new Error('Erro ao buscar estados');
        const estadosData = await estadosRes.json();
        sortedEstados = estadosData.sort((a, b) => a.nome.localeCompare(b.nome));
        setEstados(sortedEstados);
      } catch (error) {
        //console.error('Erro ao buscar estados:', error);
        message.error('Erro ao carregar lista de estados.');
      }

      if (id) {
        try {
          const response = await api.get(API_CONFIG.ENDPOINTS.FORNECEDOR.CONSULTAR_FOR_ESPECIFICO(id));
          
          if (response.data.status) {
            const fornecedorData = response.data.fornecedor;
            const formattedData = {
              razaoSocial: fornecedorData.FOR_RAZAO_SOCIAL || '',
              cnpj: fornecedorData.FOR_CNPJ || '',
              celular: fornecedorData.FOR_TELEFONE || '',
              telefone: fornecedorData.FOR_TELEFONE_BKP || '',
              email: fornecedorData.FOR_EMAIL || '',
              logradouro: fornecedorData.endereco?.END_LOGRADOURO || '',
              pais: fornecedorData.endereco?.END_PAIS || 'Brasil',
              estado: toTitleCase(fornecedorData.endereco?.END_ESTADO) || '',
              cidade: '',
              cep: fornecedorData.endereco?.END_CEP || '',
              id: fornecedorData.FOR_ID,
              status: fornecedorData.FOR_STATUS || ''
            };

            let cityToSet = '';

            if (formattedData.estado && sortedEstados.length > 0) {
              const selectedEstado = sortedEstados.find(estado => estado.nome.toLowerCase() === formattedData.estado.toLowerCase());
              if(selectedEstado) {
                try {
                  const cidadesRes = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedEstado.id}/municipios`);
                  if (!cidadesRes.ok) throw new Error('Erro ao buscar cidades do estado');
                  const cidadesData = await cidadesRes.json();
                  const sortedCidades = cidadesData.sort((a, b) => a.nome.localeCompare(b.nome));
                  setCidades(sortedCidades);

                  const apiCityName = fornecedorData.endereco?.END_CIDADE;
                  if (apiCityName) {
                    const matchedCity = sortedCidades.find(city => 
                      toTitleCase(city.nome) === toTitleCase(apiCityName)
                    );
                    if (matchedCity) {
                      cityToSet = matchedCity.nome;
                    } else {
                      cityToSet = toTitleCase(apiCityName);
                    }
                  }
                } catch (error) {
                  //console.error('Erro ao buscar cidades:', error);
                  message.error('Erro ao carregar lista de cidades.');
                }
              }
            }

            setForm({
              ...formattedData,
              cidade: cityToSet || ''
            });

          } else {
            message.error(response.data.message || 'Erro ao carregar dados do fornecedor.');
            navigate('/adm/gerenciar/fornecedor');
          }
        } catch (error) {
          //console.error('Erro ao carregar dados do fornecedor:', error);
          message.error('Erro ao carregar dados do fornecedor.');
          navigate('/adm/gerenciar/fornecedor');
        } finally {
          setLoading(false);
        }
      } else {
        message.error('ID do fornecedor não fornecido.');
        navigate('/adm/gerenciar/fornecedor');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  const formatarTelefone = (valor) => {
    const numbers = valor.replace(/\D/g, '');
    if (numbers.length <= 11) {
      if (numbers.length <= 2) {
        return numbers;
      } else if (numbers.length <= 6) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      } else if (numbers.length <= 10) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      } else {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
      }
    }
    return valor;
  };

  const formatarCEP = (valor) => {
    const numbers = valor.replace(/\D/g, '');
    if (numbers.length <= 8) {
      if (numbers.length <= 5) {
        return numbers;
      } else {
        return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
      }
    }
    return valor;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'celular' || name === 'telefone') {
      formattedValue = formatarTelefone(value);
    } else if (name === 'cep') {
      formattedValue = formatarCEP(value);
    } else if (name === 'estado') {
      const selectedEstado = estados.find(estado => estado.nome === value);
      if (selectedEstado) {
        fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedEstado.id}/municipios`)
          .then(response => response.json())
          .then(data => {
            const sortedCidades = data.sort((a, b) => a.nome.localeCompare(b.nome));
            setCidades(sortedCidades);
            setForm(prevForm => ({ ...prevForm, estado: value, cidade: '' }));
          })
          .catch(/*error => console.error('Erro ao buscar cidades:', error)*/);
      } else {
        setCidades([]);
        setForm(prevForm => ({ ...prevForm, estado: value, cidade: '' }));
      }
      setTouched({ ...touched, estado: true, cidade: false });
      return;
    }

    setForm({ ...form, [name]: formattedValue });
    setTouched({ ...touched, [name]: true });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.razaoSocial) newErrors.razaoSocial = 'Campo obrigatório';
    if (!form.cnpj) newErrors.cnpj = 'Campo obrigatório';
    //if (!form.email) newErrors.email = 'Campo obrigatório';
    //else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) newErrors.email = 'E-mail inválido';
    if (!form.celular) newErrors.celular = 'Campo obrigatório';
    if (!form.logradouro) newErrors.logradouro = 'Campo obrigatório';
    if (!form.pais) newErrors.pais = 'Campo obrigatório';
    if (!form.estado) newErrors.estado = 'Campo obrigatório';
    if (!form.cidade) newErrors.cidade = 'Campo obrigatório';
    if (!form.cep) newErrors.cep = 'Campo obrigatório';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        message.error('Sessão expirada. Por favor, faça login novamente.');
        navigate('/');
        setLoading(false);
        return;
      }

      const payload = {
        FOR_RAZAO_SOCIAL: form.razaoSocial,
        FOR_CNPJ: form.cnpj,
        FOR_TELEFONE: form.celular,
        FOR_TELEFONE_BKP: form.telefone,
        FOR_EMAIL: form.email,
        FOR_STATUS: form.status,
        END_LOGRADOURO: form.logradouro,
        END_PAIS: form.pais,
        END_ESTADO: form.estado,
        END_CIDADE: form.cidade,
        END_CEP: form.cep,
      };

      try {
        const response = await api.put(API_CONFIG.ENDPOINTS.FORNECEDOR.ATUALIZAR_FOR(form.id), payload);
        
        if (response.data && response.data.status) {
          message.success(response.data.message || 'Fornecedor atualizado com sucesso!');
          window.location.href = '/adm/gerenciar/fornecedor';
        } else {
          message.error(response.data?.message || 'Erro ao atualizar fornecedor.');
        }
      } catch (error) {
        //console.error('Erro ao salvar fornecedor:', error);
        
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          message.error('Sessão expirada. Por favor, faça login novamente.');
          window.location.href = '/';
        } else {
          message.error('Erro ao conectar com a API. Por favor, tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const showError = (field) => (submitted || touched[field]) && errors[field];

  const handleVoltar = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      message.error('Sessão expirada. Por favor, faça login novamente.');
      navigate('/login');
      return;
    }
    navigate('/adm/gerenciar/fornecedor');
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <ProtectedRoute tipoPermitido="ADM">
      <div className={`home-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
        <div className="modal-wrapper">
          <h1 className="modal-title">Editar Fornecedor</h1>
          <div className="modal-content">
            <form id="form-editar-fornecedor" className="form-fornecedor" onSubmit={handleSubmit} autoComplete="off">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Razão Social <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="razaoSocial"
                    placeholder="Razão Social"
                    value={form.razaoSocial}
                    onChange={handleChange}
                    className={showError('razaoSocial') ? 'erro' : ''}
                  />
                  {showError('razaoSocial') && <span className="msg-erro">{errors.razaoSocial}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">CNPJ <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="cnpj"
                    placeholder="CNPJ"
                    value={form.cnpj}
                    onChange={handleChange}
                    className={showError('cnpj') ? 'erro' : ''}
                  />
                  {showError('cnpj') && <span className="msg-erro">{errors.cnpj}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Telefone Celular <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="celular"
                    placeholder="(00) 00000-0000"
                    value={form.celular}
                    onChange={handleChange}
                    className={showError('celular') ? 'erro' : ''}
                    maxLength="15"
                  />
                  {showError('celular') && <span className="msg-erro">{errors.celular}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Telefone Fixo</label>
                  <input
                    type="text"
                    name="telefone"
                    placeholder="(00) 0000-0000"
                    value={form.telefone}
                    onChange={handleChange}
                    maxLength="14"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">E-mail <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="email"
                    name="email"
                    placeholder="exemplo@email.com"
                    value={form.email}
                    onChange={handleChange}
                    className={showError('email') ? 'erro' : ''}
                  />
                  {showError('email') && <span className="msg-erro">{errors.email}</span>}
                </div>
              </div>

              <label className="form-label">Endereço:</label>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Logradouro <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="logradouro"
                    placeholder="Rua, Bairro, Número"
                    value={form.logradouro}
                    onChange={handleChange}
                    className={showError('logradouro') ? 'erro' : ''}
                  />
                  {showError('logradouro') && <span className="msg-erro">{errors.logradouro}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">País <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="pais"
                    placeholder="Brasil"
                    value={form.pais}
                    onChange={handleChange}
                    className={showError('pais') ? 'erro' : ''}
                    readOnly
                  />
                  {showError('pais') && <span className="msg-erro">{errors.pais}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Estado <span className="asterisco-obrigatorio">*</span></label>
                  <select
                    name="estado"
                    value={form.estado}
                    onChange={handleChange}
                    className={showError('estado') ? 'erro' : ''}
                  >
                    <option value="">Selecione o Estado</option>
                    {estados.map(estado => (
                      <option key={estado.id} value={estado.nome}>
                        {estado.nome}
                      </option>
                    ))}
                  </select>
                  {showError('estado') && <span className="msg-erro">{errors.estado}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Cidade <span className="asterisco-obrigatorio">*</span></label>
                  <select
                    name="cidade"
                    value={form.cidade}
                    onChange={handleChange}
                    className={showError('cidade') ? 'erro' : ''}
                    disabled={cidades.length === 0}
                  >
                    <option value="">Selecione a Cidade</option>
                    {cidades.map(cidade => (
                      <option key={cidade.id} value={cidade.nome}>
                        {cidade.nome}
                      </option>
                    ))}
                  </select>
                  {showError('cidade') && <span className="msg-erro">{errors.cidade}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">CEP <span className="asterisco-obrigatorio">*</span></label>
                  <input
                    type="text"
                    name="cep"
                    placeholder="00000-000"
                    value={form.cep}
                    onChange={handleChange}
                    className={showError('cep') ? 'erro' : ''}
                    maxLength="9"
                  />
                  {showError('cep') && <span className="msg-erro">{errors.cep}</span>}
                </div>
              </div>
            </form>

            <div className="form-actions">
              <Button
                className="submit-button"
                type="primary"
                icon={<ArrowLeftOutlined />}
                onClick={handleVoltar}
                style={{ minWidth: 120, fontWeight: 500, background: '#001f4d', border: 'none' }}
              >
                Voltar
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="submit-button" 
                style={{ minWidth: 140 }}
                loading={loading}
              >
                Salvar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default EditarFornecedor; 